import { useState } from 'react';
import {
    Button, Card, FieldError, Input, Label, Modal, SearchField, Skeleton,
    Tag, TagGroup, TagGroupList, TextArea, TextField
} from '@heroui/react';

import { StatusChip, VisibilityChip, ArchivedChip, AdminChip, RoleChip, TagChip, TopicChip, TaskStatusChip, DeliveryStateChip } from '../components/common/StatusChip';
import Notice from '../components/common/Notice';
import PasswordField from '../components/common/PasswordField';
import { AuthLayout, AuthLink } from '../components/common/AuthLayout';
import { LoadMore, EmptyResult } from '../components/common/LoadMore';
import Pagination from '../components/common/Pagination';
import TagsField from '../components/common/TagsField';
import SuggestionsField from '../components/common/SuggestionsField';
import TimeAgo from '../components/common/TimeAgo';
import ViewTab from '../components/common/ViewTab';
import AvatarStack from '../components/common/AvatarStack';
import CommentBubble from '../components/common/CommentBubble';
import MentionText from '../components/common/MentionText';
import MentionTextArea from '../components/common/MentionTextArea';
import UserBadge from '../components/common/UserBadge';
import { SkeletonCard, SkeletonThumbGrid } from '../components/common/Skeleton';
import RelevanceChip from '../components/common/RelevanceChip';
import FieldWithAction from '../components/common/FieldWithAction';
import FundingFields from '../components/common/FundingFields';
import FundingFilters from '../components/common/FundingFilters';
import FundingSummary from '../components/common/FundingSummary';
import RelatedResources from '../components/common/RelatedResources';
import DiscardedCuration from '../components/common/DiscardedCuration';
import ActivityItem from '../components/common/ActivityItem';
import ExportJsonLd from '../components/common/ExportJsonLd';
import ActiveFilters from '../components/common/ActiveFilters';
import TaskBoard from '../components/private/tasks/TaskBoard';
import BackToTop from '../components/common/BackToTop';
import { readBrowseState } from '../services/browseState';
import { TASKS } from '../i18n/el';

// ΒΙΒΛΙΟΘΗΚΗ ΣΥΣΤΑΤΙΚΩΝ, στη διαδρομή /design-check. **Μόνιμη**, όχι προσωρινή.
//
// Ήταν προσωρινή οθόνη της μετάβασης σε HeroUI και διαγράφηκε όταν ολοκληρώθηκαν οι
// έλεγχοι που την χρειάζονταν. Επανήλθε στις 18/08 με διαφορετικό ρόλο: **μία οθόνη
// που δείχνει κάθε κοινό συστατικό με τις πραγματικές σχεδιαστικές σταθερές**.
//
// Ο λόγος είναι πρακτικός και επαναλαμβανόμενος. Οι έλεγχοι πληκτρολογίου, αντίθεσης
// και τριών πλατών γίνονται σε **μία** σελίδα αντί για δεκαπέντε, και επαναλαμβάνονται
// σε κάθε αλλαγή της παλέτας ή αναβάθμιση του HeroUI. Χωρίς αυτήν, κάθε τέτοιος έλεγχος
// απαιτεί περιήγηση σε όλη την εφαρμογή με δεδομένα που να ενεργοποιούν κάθε κατάσταση.
//
// **Απαιτεί σύνδεση** και ζει μέσα στο κέλυφος του Dashboard: δεν είναι δημόσια σελίδα
// και δεν αποκαλύπτει τη δομή της διεπαφής σε μη συνδεδεμένο επισκέπτη.
//
// **Η πληρότητά της επιβάλλεται από δοκιμή.** Το `designCheck.test.js` διαβάζει τον
// κατάλογο των αρχείων του `components/common` και πέφτει αν κάποιο δεν εμφανίζεται
// εδώ — δεν απαριθμεί ονόματα, ίδιο σκεπτικό με το `CURATION_FIELDS`. Συστατικό που
// προστίθεται αύριο πρέπει να μπει και εδώ.
//
// Επαληθεύει ότι τα συστατικά αποδίδουν, ότι παίρνουν την παλέτα, ότι η γραμματοσειρά
// φορτώνει τοπικά με ελληνικά, και ότι το focus ring είναι ορατό.
//
// Οι ενότητες αντιστοιχούν στα ευρήματα B έως I του ελέγχου της 17/08 και είναι
// σημειωμένες, ώστε ο έλεγχος να είναι απαρίθμηση και όχι εντύπωση.
//
// Αφαιρείται στο τελευταίο επίπεδο της μετάβασης.

/** Τριάντα ετικέτες σε πραγματικό μήκος: το δείγμα πυκνότητας του ευρήματος H. */
const THIRTY_TAGS = [
    'έρευνα', 'digital humanities', 'horizon 2026', 'ανοιχτά δεδομένα', 'μεταδεδομένα',
    'ψηφιοποίηση', 'OCR', 'αρχειονομία', 'βιβλιοθηκονομία', 'linked data',
    'οντολογίες', 'SPARQL', 'ταξινόμηση', 'θησαυροί όρων', 'Dublin Core',
    'χρηματοδότηση', 'ΕΣΠΑ', 'Erasmus+', 'διακρατικά', 'αξιολόγηση',
    'ανάκτηση πληροφορίας', 'μηχανική μάθηση', 'επεξεργασία φυσικής γλώσσας',
    'οπτικοποίηση', 'προσβασιμότητα', 'WCAG', 'διεπαφές χρήστη', 'αξιοποίηση',
    'διατήρηση', 'πνευματικά δικαιώματα'
];

/** Οι δύο παραλλαγές του ευρήματος H, με τα ονόματα που έχουν στο CSS. */
const APPEARANCES = [
    { key: 'soft', title: 'Παραλλαγή Α — απαλό φόντο', note: 'Το χρώμα κάνει τη διάκριση ορατή από μακριά. Τριάντα μαζί δίνουν τριάντα χρωματιστά μπλοκ.' },
    { key: 'outline', title: 'Παραλλαγή Β — περίγραμμα', note: 'Λευκή επιφάνεια με χρωματιστό περίγραμμα. Ήπιο σε πυκνότητα, λιγότερο εμφατικό μεμονωμένα.' }
];

function Section({ finding, title, note, children, className = '' }) {
    return (
        <section className={`mb-10 ${className}`}>
            <h2 className="mb-1 flex items-baseline gap-2 text-lg font-medium">
                <span className="rounded-full bg-accent-soft px-2 py-0.5 text-sm font-semibold text-accent-soft-foreground">
                    {finding}
                </span>
                {title}
            </h2>
            {note && <p className="mb-3 max-w-2xl text-sm text-muted">{note}</p>}
            {children}
        </section>
    );
}

/**
 * Δείγματα γραμμής δραστηριότητας (P1-7).
 *
 * Οι χρόνοι είναι **σχετικοί ως προς τη στιγμή απόδοσης** και όχι σταθερές
 * ημερομηνίες: το `TimeAgo` δείχνει «πριν από…», οπότε μια καρφωμένη ημερομηνία θα
 * έδειχνε «πριν από 8 μήνες» σε λίγο καιρό και το δείγμα θα έπαυε να δείχνει τη
 * μορφή που ελέγχεται.
 */
const minutesAgo = (minutes) => new Date(Date.now() - minutes * 60000).toISOString();

/** Χειριστής χωρίς ενέργεια, ως **όνομα**: η βιβλιοθήκη δείχνει μορφή, όχι ροή. */
const noop = () => {};

/**
 * Δείγματα καθηκόντων για τον πίνακα (Σετ Β).
 *
 * Ο δεύτερος έχει **ανάδοχο** τον `u2` και ο τρίτος περασμένη προθεσμία: οι δύο
 * καταστάσεις που η κάρτα οφείλει να δείχνει διαφορετικά, και που θα ήταν
 * αόρατες σε ομοιόμορφο δείγμα.
 */
const BOARD_SAMPLES = [
    { id: 'bt1', title: 'Σάρωση προκηρύξεων ΕΛΙΔΕΚ', status: 'todo', assignees: [], dueDate: null },
    {
        id: 'bt2', title: 'Έλεγχος επιλεξιμότητας', status: 'in_progress',
        assignees: [{ id: 'u2', username: 'annamaria' }], dueDate: minutesAgo(-60 * 24 * 20)
    },
    {
        id: 'bt3', title: 'Σύνταξη περίληψης', status: 'done',
        assignees: [{ id: 'u2', username: 'annamaria' }], dueDate: minutesAgo(60 * 24 * 3)
    }
];

/**
 * Πέντε πρόσωπα, ώστε το δεύτερο δείγμα να **ξεπερνά** το όριο εμφάνισης.
 *
 * Δείγμα με τρία ή λιγότερα θα έδειχνε τη στοίβα και **ποτέ** τον μετρητή, που
 * είναι το μόνο σκέλος με λογική μέσα του.
 */
const AVATAR_STACK_SAMPLE = [
    { id: 'p1', fullName: 'Μαρία Παπαδοπούλου', username: 'mpapadopoulou' },
    { id: 'p2', fullName: 'Άννα-Μαρία Δ.', username: 'annamaria' },
    { id: 'p3', username: 'anonymos' },
    { id: 'p4', fullName: 'Γιώργος Κ.', username: 'gkappa' },
    { id: 'p5', fullName: 'Νίκος Λ.', username: 'nlambda' }
];

const SAMPLE_ACTOR = { id: '1', username: 'annamaria', fullName: 'Άννα-Μαρία Δ.', avatar: '' };

const ACTIVITY_SAMPLES = {
    read: {
        id: 'a1', count: 1, unread: 0, verb: 'comment.created', objectType: 'comment',
        objectId: 'r1', object: { title: 'Πρόσκληση ΕΛΙΔΕΚ 2026' }, summary: '',
        createdAt: minutesAgo(9), actor: SAMPLE_ACTOR, collection: 'c1'
    },
    unread: {
        id: 'a2', count: 1, unread: 1, verb: 'member.added', objectType: 'user',
        objectId: 'u1', object: { username: 'george' }, summary: '',
        createdAt: minutesAgo(42), actor: SAMPLE_ACTOR, collection: 'c1'
    },
    grouped: {
        id: 'a3', count: 3, unread: 3, verb: 'resource.updated_elsewhere', objectType: 'resource',
        objectId: 'r2', object: { title: 'Horizon Europe — Cluster 2' },
        summary: 'Αποσυνδέθηκε ο πόρος. Διαγράφηκαν 4 σχόλια και 2 αξιολογήσεις αυτής της συλλογής.',
        createdAt: minutesAgo(180), actor: SAMPLE_ACTOR, collection: null
    },
    /**
     * D44: **η ίδια φράση με την πρώτη**, και σκόπιμα.
     *
     * Οι δύο γραμμές είναι το ζεύγος που κάνει τη σήμανση απαραίτητη: ίδιο ρήμα,
     * ίδιο αντικείμενο, ίδιος δράστης — και το μόνο που τις ξεχωρίζει είναι **ο
     * λόγος για τον οποίο τις βλέπει ο συγκεκριμένος αναγνώστης**. Χωρίς τη
     * σήμανση, ο αναφερόμενος δεν έχει τρόπο να το καταλάβει.
     */
    mentioned: {
        id: 'a4', count: 1, unread: 1, verb: 'comment.created', objectType: 'comment',
        objectId: 'r1', object: { title: 'Πρόσκληση ΕΛΙΔΕΚ 2026' }, summary: '',
        createdAt: minutesAgo(4), actor: SAMPLE_ACTOR, collection: 'c1', reason: 'mention'
    }
};

/**
 * ΛΑ-12: το δείγμα των συσχετίσεων.
 *
 * Ο **τρίτος** συσχετισμένος δεν υπάρχει στη λίστα, σκόπιμα: είναι ο πόρος που
 * ζει σε άλλη σελίδα του καταλόγου, και η οθόνη πρέπει να τον δείχνει ως τέτοιον
 * αντί για κενή ετικέτα.
 */
const RELATED_SAMPLE = {
    resource: { id: 'r1', relatedResources: ['r2', 'r3', 'r7'] },
    resources: [
        { id: 'r1', title: 'Πρόσκληση ΕΛΙΔΕΚ 2026' },
        { id: 'r2', title: 'Οδηγός υποβολής προτάσεων' },
        { id: 'r3', title: 'Horizon Europe: το ίδιο πεδίο' },
        { id: 'r4', title: 'Άσχετος πόρος, για τον επιλογέα' }
    ]
};

/**
 * P1-5β: το δείγμα των απορριφθέντων.
 *
 * Περιλαμβάνει **κενή τρέχουσα τιμή** (η εικόνα) και **μακρύ κείμενο** (οι όροι):
 * τα δύο σχήματα που σπάνε τον πίνακα σε στενή οθόνη, αν σπάσει.
 */
const DISCARDED_SAMPLE = [
    { field: 'title', submitted: 'Πρόσκληση ΕΛΙΔΕΚ 2026 — δεύτερος κύκλος', current: 'Πρόσκληση ΕΛΙΔΕΚ 2026' },
    { field: 'image', submitted: 'https://example.org/banner.png', current: '' },
    { field: 'funding.agency', submitted: 'Ευρωπαϊκή Επιτροπή', current: 'ΕΛΙΔΕΚ' },
    {
        field: 'funding.terms',
        submitted: 'Υποβολή έως τη λήξη των πιστώσεων, με ηλεκτρονική αίτηση και συνοδευτικά δικαιολογητικά',
        current: 'Έως τη λήξη των πιστώσεων'
    }
];

export default function DesignCheck() {
    // Οι ετικέτες που αφαιρούνται πραγματικά, ώστε ο έλεγχος του ευρήματος G να
    // είναι λειτουργικός και όχι οπτικός.
    const [tags, setTags] = useState(['έρευνα', 'digital humanities', 'horizon 2026', 'ανοιχτά δεδομένα']);
    // Οι προτάσεις της D49: **ελληνικά και λατινικά μαζί**, και δύο από αυτές
    // (`έρευνα`, `horizon 2026`) ήδη επιλεγμένες — η περιοχή οφείλει να τις
    // κρύψει, αλλιώς προσφέρει διπλότυπο. Το `Έρευνα` διαφέρει μόνο σε
    // **πεζά-κεφαλαία** και κρύβεται επίσης· το `ερευνα` **χωρίς τόνο** μένει
    // ορατό, και είναι το δηλωμένο όριο: εξομάλυνση τονισμού δεν γίνεται εδώ.
    const TAG_SUGGESTIONS = ['έρευνα', 'Έρευνα', 'ερευνα', 'προθεσμία 2026', 'horizon 2026'];
    // Οι ειδοποιήσεις που κλείνουν, για τον ίδιο λόγο.
    const [dismissed, setDismissed] = useState([]);
    const [password, setPassword] = useState('');
    // Η εμφάνιση των καρτών τρέχει μία φορά στο mount. Χωρίς τρόπο να ξαναπαίξει,
    // ο έλεγχος θα απαιτούσε ανανέωση της σελίδας.
    const [riseKey, setRiseKey] = useState(0);
    const [funding, setFunding] = useState({
        agency: 'ΕΛΙΔΕΚ',
        field: 'Επιστήμες Μηχανικού',
        deadline: '2026-12-15',
        amount: 250000,
        eligibleEntities: 'ΑΕΙ και ερευνητικά κέντρα',
        terms: 'Η πρόταση υποβάλλεται από ερευνητική ομάδα.'
    });
    // Το παράθυρο της βιβλιοθήκης: **με περιεχόμενο που ΔΕΝ χωρά**, γιατί αυτό
    // ακριβώς είναι η κατάσταση που δύο φορές έφτασε σπασμένη στην οθόνη.
    const [isModalOpen, setModalOpen] = useState(false);
    // Το πεδίο με κουμπί δίπλα: η εναλλαγή δείχνει ότι το κουμπί ΔΕΝ κουνιέται.
    const [showFieldError, setShowFieldError] = useState(true);
    const [mentionDraft, setMentionDraft] = useState('Δες το @');
    const [fundingFilters, setFundingFilters] = useState({
        agency: '', field: '', deadlineBefore: '', minAmount: '', eligible: '', relevance: ''
    });

    const removeTags = (keys) => setTags((current) => current.filter((tag) => !keys.has(tag)));
    const dismiss = (status) => setDismissed((current) => [...current, status]);

    return (
        <main className="min-h-screen bg-background p-8 font-sans text-foreground">
            <h1 className="mb-2 text-2xl font-semibold">Έλεγχος σχεδιασμού</h1>
            <p className="mb-10 max-w-2xl text-sm text-muted">
                Ελληνικά και latin σε Google Sans, τοπικά. Πάτα <kbd className="rounded border border-border bg-surface px-1.5 py-0.5 text-xs">Tab</kbd> για
                να δεις το focus ring σε κάθε στοιχείο. Τα σήματα Α έως Ι αντιστοιχούν στα ευρήματα
                του ελέγχου της 17/08.
            </p>

            <Section
                finding="C"
                title="Κουμπιά, πλήρως στρογγυλά"
                note="Το focus ring ακολουθεί το σχήμα χωρίς παρέμβαση: το ring του Tailwind είναι box-shadow, και το box-shadow κληρονομεί το border-radius του στοιχείου."
            >
                <div className="flex flex-wrap items-center gap-3">
                    <Button>Πρωτεύον</Button>
                    <Button variant="secondary">Δεύτερο</Button>
                    <Button variant="outline">Περίγραμμα</Button>
                    <Button variant="ghost">Ήπιο</Button>
                    <Button variant="danger">Διαγραφή</Button>
                    <Button isDisabled>Ανενεργό</Button>
                    <Button isPending>Σε εξέλιξη</Button>
                    <Button isIconOnly aria-label="Προσθήκη πόρου">
                        <i className="fa-solid fa-plus" aria-hidden="true" />
                    </Button>
                </div>
                <p className="mt-3 max-w-2xl text-sm text-muted">
                    Και οι τρεις μεσαίες παραλλαγές είχαν την ίδια αιτία: το HeroUI τις στηρίζει στο
                    ουδέτερο γκρι 94%, που πάνω στον καμβά δεν ξεχωρίζει. Το περίγραμμα ήταν
                    <strong className="text-foreground"> 1.15:1</strong> έναντι του λευκού, δηλαδή κάτω από το μισό
                    του κατωφλίου. Το «Δεύτερο» φέρει τώρα το δεύτερο χρώμα της παλέτας.
                </p>
            </Section>

            <Section
                finding="B"
                title="Μωβ και κυανοπράσινο, δίπλα δίπλα"
                note="Το δεύτερο χρώμα είναι #174F50: 9.25:1 σε λευκό, 8.04:1 ως chip. Το κρίσιμο δεν είναι κάθε χρώμα χωριστά αλλά αν τα δύο συνυπάρχουν σε μικρά στοιχεία χωρίς να συγκρούονται."
            >
                <div className="flex flex-col gap-4">
                    <div className="flex flex-wrap items-center gap-2">
                        <VisibilityChip visibility="private" />
                        <VisibilityChip visibility="public" />
                        <TagChip>μεταδεδομένα</TagChip>
                        <StatusChip icon="fa-solid fa-folder-open" tone="teal">Ανοιχτό</StatusChip>
                        <TagChip>ψηφιοποίηση</TagChip>
                        <StatusChip icon="fa-solid fa-users" tone="teal">Τρία μέλη</StatusChip>
                    </div>

                    <div className="flex flex-wrap items-center gap-5 text-xl">
                        <i className="fa-solid fa-lock text-purple-800" aria-hidden="true" />
                        <i className="fa-solid fa-globe text-teal-600" aria-hidden="true" />
                        <i className="fa-solid fa-layer-group text-purple-800" aria-hidden="true" />
                        <i className="fa-solid fa-users text-teal-600" aria-hidden="true" />
                        <i className="fa-solid fa-hashtag text-purple-800" aria-hidden="true" />
                        <i className="fa-solid fa-list-check text-teal-600" aria-hidden="true" />
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        <Button>Δημιουργία συλλογής</Button>
                        <Button className="bg-teal-600 text-white hover:bg-teal-700">Πρόσκληση μέλους</Button>
                        <Button variant="outline">Ακύρωση</Button>
                    </div>

                    <div className="flex flex-wrap gap-3">
                        {[['purple', '#4C004C', '14.88:1'], ['teal', '#174F50', '9.25:1']].map(([name, hex, ratio]) => (
                            <div key={name} className="w-44 overflow-hidden rounded-md border border-border">
                                <div className={`px-3 py-4 text-sm font-medium text-white ${name === 'purple' ? 'bg-purple-800' : 'bg-teal-600'}`}>
                                    Λευκό σε {hex}
                                </div>
                                <div className="bg-surface px-3 py-2 text-sm text-muted">{ratio} — AAA</div>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            <Section
                finding="H"
                title="Chips: δύο παραλλαγές προς επιλογή"
                note="Οι ίδιοι τόνοι στις δύο παραλλαγές. Και οι δύο τηρούν τους τρεις κανόνες: σκούρο κείμενο, υποχρεωτικό εικονίδιο, κεφαλαίο αρχικό."
            >
                <div className="grid gap-6 lg:grid-cols-2">
                    {APPEARANCES.map(({ key, title, note }) => (
                        <Card key={key}>
                            <Card.Header>
                                <Card.Title>{title}</Card.Title>
                                <Card.Description>{note}</Card.Description>
                            </Card.Header>
                            <Card.Content className="flex flex-col gap-4">
                                <div className="flex flex-wrap items-center gap-2">
                                    <VisibilityChip visibility="private" appearance={key} />
                                    <VisibilityChip visibility="public" appearance={key} />
                                    <ArchivedChip appearance={key} />
                                    <RoleChip role="coordinator" appearance={key} />
                                    <RoleChip role="member" appearance={key} />
                                    {/* Σετ Γ: ο **καθολικός** ρόλος, δίπλα στους ρόλους
                                        συλλογής και όχι ανάμεσά τους — η θέση δείχνει ότι
                                        είναι άλλο είδος πράγματος: ιδιότητα του
                                        λογαριασμού και όχι της συμμετοχής. */}
                                    <AdminChip isAdmin appearance={key} />
                                    <TopicChip appearance={key}>Έρευνα</TopicChip>
                                </div>

                                {/* ΛΑ-19, P1-4. Και οι **τρεις** καταστάσεις μαζί: το
                                    ζητούμενο δεν είναι ότι το chip αποδίδεται, αλλά ότι
                                    ξεχωρίζουν μεταξύ τους — και σε ασπρόμαυρο, όπου μένει
                                    μόνο το εικονίδιο. */}
                                <div className="flex flex-wrap items-center gap-2">
                                    <TaskStatusChip status="todo" appearance={key} />
                                    <TaskStatusChip status="in_progress" appearance={key} />
                                    <TaskStatusChip status="done" appearance={key} />
                                </div>

                                {/* ΛΑ-27. Οι **πέντε** καταστάσεις παράδοσης, μαζί και
                                    στην ίδια πυκνότητα. Το κρίσιμο ζεύγος είναι
                                    «Απέτυχε» και «Δεν στάλθηκε»: μοιάζουν, και σημαίνουν
                                    αντίθετα πράγματα για τον διαχειριστή — η πρώτη είναι
                                    πρόβλημα του τρίτου συστήματος, η δεύτερη **απόφαση
                                    του δικού μας**. Αν δεν ξεχωρίζουν εδώ, δεν θα
                                    ξεχωρίσουν σε κατάλογο σαράντα γραμμών. */}
                                <div className="flex flex-wrap items-center gap-2">
                                    <DeliveryStateChip state="pending" appearance={key} />
                                    <DeliveryStateChip state="delivering" appearance={key} />
                                    <DeliveryStateChip state="succeeded" appearance={key} />
                                    <DeliveryStateChip state="failed" appearance={key} />
                                    <DeliveryStateChip state="suppressed" appearance={key} />
                                </div>

                                <div>
                                    <p className="mb-2 text-sm font-medium">Τριάντα ετικέτες, πραγματική πυκνότητα</p>
                                    <div className="flex flex-wrap gap-1.5 rounded-md border border-border bg-surface p-3">
                                        {THIRTY_TAGS.map((tag) => (
                                            <TagChip key={tag} appearance={key}>{tag}</TagChip>
                                        ))}
                                    </div>
                                </div>
                            </Card.Content>
                        </Card>
                    ))}
                </div>
            </Section>

            <Section
                finding="E"
                title="Ειδοποιήσεις με χρωματισμένο φόντο"
                note="Τέσσερα σήματα ταυτόχρονα: απαλό φόντο, σκούρο κείμενο, εικονίδιο, αριστερό περίγραμμα στο έντονο χρώμα. Η περιγραφή παίρνει το κύριο χρώμα κειμένου και όχι το δεύτερης τάξης: πάνω σε χρωματισμένο φόντο το γκρι διαβαζόταν ως αχνό, και οι αναλογίες ανέβηκαν από 6.5–7:1 σε 13.5–14.6:1."
                className="max-w-2xl"
            >
                <div className="flex flex-col gap-3">
                    {[
                        ['accent', 'Ενημέρωση', 'Η συλλογή δημιουργήθηκε και είναι ιδιωτική.', true],
                        ['success', 'Επιτυχία', 'Το προφίλ σας ενημερώθηκε.', true],
                        ['warning', 'Προσοχή', 'Η συλλογή είναι αρχειοθετημένη: δεν δέχεται νέους πόρους.', true],
                        ['danger', 'Σφάλμα', 'Η διεύθυνση υπάρχει ήδη σε αυτή τη συλλογή.', false]
                    ].filter(([status]) => !dismissed.includes(status))
                        .map(([status, title, description, canDismiss]) => (
                            <Notice
                                key={status}
                                status={status}
                                title={title}
                                onDismiss={canDismiss ? () => dismiss(status) : undefined}
                            >
                                {description}
                            </Notice>
                        ))}
                </div>

                <p className="mt-3 max-w-2xl text-sm text-muted">
                    Οι τρεις πρώτες κλείνουν. Το <strong className="text-foreground">σφάλμα δεν κλείνει</strong>, και
                    ο κανόνας δεν είναι αισθητικός: το μήνυμα είναι ο λόγος για τον οποίο η φόρμα δεν
                    προχώρησε. Αν φύγει, ο χρήστης μένει με φόρμα που δεν υποβάλλεται και κανένα στοιχείο
                    για το γιατί.
                </p>

                {dismissed.length > 0 && (
                    <Button className="mt-3" variant="outline" onPress={() => setDismissed([])}>
                        Επαναφορά των {dismissed.length} ειδοποιήσεων
                    </Button>
                )}
            </Section>

            <Section
                finding="F"
                title="Πεδία, με ορατό περίγραμμα"
                note="Το HeroUI δεν βάζει περίγραμμα στα πεδία: --field-border-width είναι 0 και --field-border transparent. Το περίγραμμα #8E8794 δίνει 3.47:1 έναντι του πεδίου και 3.19:1 έναντι του καμβά, δηλαδή WCAG 1.4.11 και στα δύο φόντα. Τα placeholder σκούραιναν από 5.69:1 σε 7.78:1 και χρησιμοποιούν το ίδιο χρώμα με το δεύτερης τάξης κείμενο· παραμένουν ανοιχτότερα από συμπληρωμένο περιεχόμενο, που είναι 16.26:1. Η λειτουργία της αναζήτησης είναι P1-5: εδώ ελέγχεται μόνο η εμφάνιση."
                className="max-w-md"
            >
                <div className="flex flex-col gap-4">
                    <SearchField>
                        <Label>Αναζήτηση συλλογών</Label>
                        <SearchField.Group>
                            <SearchField.SearchIcon />
                            <SearchField.Input placeholder="Τίτλος ή θεματικό αντικείμενο" />
                            <SearchField.ClearButton />
                        </SearchField.Group>
                    </SearchField>

                    <TextField>
                        <Label>Τίτλος</Label>
                        <Input placeholder="Χρηματοδοτικές ευκαιρίες" />
                    </TextField>

                    <TextField defaultValue="Χρηματοδοτικές ευκαιρίες 2026">
                        <Label>Τίτλος, συμπληρωμένος</Label>
                        <Input />
                    </TextField>

                    <TextField isInvalid>
                        <Label>Διεύθυνση</Label>
                        <Input placeholder="https://..." />
                        <FieldError>Η διεύθυνση είναι υποχρεωτική</FieldError>
                    </TextField>

                    <TextField>
                        <Label>Περιγραφή</Label>
                        <TextArea placeholder="Σύντομη περιγραφή" />
                    </TextField>
                </div>
            </Section>

            <Section
                finding="8"
                title="Πεδίο κωδικού με εναλλαγή ορατότητας"
                note="Θα χρησιμοποιηθεί στις οθόνες σύνδεσης και εγγραφής, που μεταφέρονται στο επόμενο επίπεδο. Το κουμπί αλλάζει και το aria-label μαζί με την κατάσταση, και ανακοινώνει ποια είναι με aria-pressed: ένα σταθερό «Εμφάνιση κωδικού» λέει ψέματα στη μισή διάρκεια ζωής του."
                className="max-w-md"
            >
                <div className="flex flex-col gap-4">
                    <PasswordField
                        label="Κωδικός"
                        value={password}
                        onChange={setPassword}
                        placeholder="Ο κωδικός σας"
                    />
                    <PasswordField
                        label="Επιβεβαίωση κωδικού"
                        name="passwordConfirm"
                        autoComplete="new-password"
                        isInvalid
                        errorMessage="Οι δύο κωδικοί δεν ταιριάζουν"
                    />
                </div>
            </Section>

            <Section
                finding="G"
                title="Ετικέτες με αφαίρεση"
                note="Το Χ εμφανίζεται μόνο όταν το TagGroup δέχεται onRemove. Ο στόχος είναι 24×24 πίξελ ενώ το γλυφικό μένει μικρό, και το aria-label ονομάζει την ετικέτα αντί να λέει «Remove tag»."
                className="max-w-md"
            >
                <div className="flex flex-col gap-5">
                    <div>
                        <p className="mb-2 text-sm font-medium">Επεξεργάσιμες — {tags.length} ετικέτες</p>
                        <TagGroup aria-label="Ετικέτες πόρου" onRemove={removeTags}>
                            <TagGroupList>
                                {tags.map((tag) => (
                                    <Tag key={tag} id={tag}>
                                        {tag}
                                        <Tag.RemoveButton aria-label={`Αφαίρεση της ετικέτας ${tag}`} />
                                    </Tag>
                                ))}
                            </TagGroupList>
                        </TagGroup>
                        {tags.length === 0 && (
                            <Button className="mt-3" variant="outline" onPress={() => setTags(['έρευνα', 'digital humanities', 'horizon 2026', 'ανοιχτά δεδομένα'])}>
                                Επαναφορά δείγματος
                            </Button>
                        )}
                    </div>

                    <div>
                        <p className="mb-2 text-sm font-medium">Μόνο για προβολή — χωρίς Χ</p>
                        <TagGroup aria-label="Ετικέτες προβολής">
                            <TagGroupList>
                                <Tag id="a">έρευνα</Tag>
                                <Tag id="b">digital humanities</Tag>
                                <Tag id="c">horizon 2026</Tag>
                            </TagGroupList>
                        </TagGroup>
                    </div>
                </div>
            </Section>

            <Section
                finding="I"
                title="Μεταβάσεις και σκελετοί"
                note="Ανύψωση 4 πίξελ σε hover με αλλαγή σκιάς, εμφάνιση με κλιμακωτή μετατόπιση, μεταβάσεις chip και κουμπιών. Το shimmer του σκελετού έτρεχε ήδη αλλά ήταν αόρατο: η λάμψη ήταν το ίδιο γκρι με τη βάση, σε διαφορά 30% διαφάνειας."
            >
                <div className="mb-4 flex flex-wrap items-center gap-3">
                    <Button variant="outline" onPress={() => setRiseKey((n) => n + 1)}>
                        <i className="fa-solid fa-rotate-right" aria-hidden="true" />
                        Επανάληψη της εμφάνισης
                    </Button>
                    <span className="text-sm text-muted">
                        Αν δεν κινείται τίποτα, το λειτουργικό έχει απενεργοποιημένα τα εφέ κίνησης και το
                        <code className="mx-1 rounded bg-surface px-1 py-0.5 text-xs">prefers-reduced-motion</code>
                        τα ακυρώνει σωστά.
                    </span>
                </div>

                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {['Χρηματοδοτικές ευκαιρίες', 'Ψηφιακές συλλογές', 'Ανοιχτά δεδομένα'].map((title, index) => (
                        <Card
                            key={`${title}-${riseKey}`}
                            className="card--interactive ds-rise"
                            style={{ animationDelay: `${index * 90}ms` }}
                        >
                            <Card.Header>
                                <Card.Title>{title}</Card.Title>
                                <Card.Description>Δύο πόροι, ένα μέλος</Card.Description>
                            </Card.Header>
                            <Card.Content className="flex flex-wrap gap-2">
                                <VisibilityChip visibility={index === 1 ? 'public' : 'private'} />
                                <RoleChip role="coordinator" />
                            </Card.Content>
                        </Card>
                    ))}

                    <Card>
                        <Card.Content className="flex flex-col gap-3">
                            <Skeleton className="h-6 w-3/5 rounded" />
                            <Skeleton className="h-4 w-full rounded" />
                            <Skeleton className="h-4 w-4/5 rounded" />
                            <Skeleton className="h-8 w-32 rounded-full" />
                        </Card.Content>
                    </Card>
                </div>

                {/* Τα ΔΙΚΑ ΜΑΣ σκελετικά, από το components/common/Skeleton.jsx. Το
                    παραπάνω είναι το πρωτογενές του HeroUI· αυτά είναι που αποδίδονται
                    πραγματικά στις οθόνες, και η βιβλιοθήκη οφείλει να δείχνει αυτά. */}
                <div className="mt-6 flex flex-col gap-4">
                    <p className="text-sm text-muted">SkeletonCard — μία κάρτα πόρου σε φόρτωση</p>
                    <div className="max-w-sm"><SkeletonCard /></div>

                    <p className="text-sm text-muted">SkeletonThumbGrid — η επισκόπηση συλλογών σε φόρτωση</p>
                    <SkeletonThumbGrid count={3} />
                </div>
            </Section>

            <Section
                finding="D"
                title="Δεύτερης τάξης κείμενο"
                note="Το προηγούμενο #6B6472 έδινε 5.69:1 και δεν διαβαζόταν σε μικρό μέγεθος. Το #56505C δίνει 7.78:1 σε λευκό και 7.14:1 σε καμβά, και χρησιμοποιείται και για τα placeholder. Ελάχιστο μέγεθος 14 πίξελ: κάτω από αυτό χρησιμοποιείται το κύριο χρώμα."
                className="max-w-2xl"
            >
                <div className="flex flex-col gap-3 rounded-md border border-border bg-surface p-4">
                    <p className="text-xl font-bold">22 πίξελ σε βάρος 700 — τίτλος κάρτας, όπως στις κάρτες παραπάνω</p>
                    <p className="text-base">16 πίξελ, κύριο χρώμα — Θεματική περιήγηση ψηφιακών πόρων</p>
                    <p className="text-sm text-muted">14 πίξελ, δεύτερης τάξης #56505C — το ελάχιστο επιτρεπτό μέγεθος για αυτό το χρώμα</p>
                    <p className="text-xs">12 πίξελ, κύριο χρώμα #241E28 — επιτρεπτό, γιατί το χρώμα δεν είναι δεύτερης τάξης</p>
                </div>
            </Section>

            <Section
                finding="—"
                title="Κέλυφος ταυτοποίησης, ετικέτες, σχετικός χρόνος και σελιδοποίηση"
                note="Τα υπόλοιπα κοινά συστατικά. Η ενότητα υπάρχει ώστε η σελίδα να καλύπτει ΚΑΘΕ αρχείο του components/common, όπως απαιτεί το designCheck.test.js."
            >
                <div className="flex flex-col gap-6">
                    <div>
                        <p className="mb-2 text-sm text-muted">
                            TagsField — με αφαίρεση, στόχος 24×24, και τις προτάσεις της D49
                        </p>
                        <TagsField value={tags} onChange={setTags} suggestions={TAG_SUGGESTIONS} />
                    </div>

                    <div>
                        <p className="mb-2 text-sm text-muted">
                            SuggestionsField — μόνη της, χωρίς ελεύθερη τιμή. Η υπόδειξη
                            αλλάζει: εκεί όπου ο χρήστης ΔΕΝ μπορεί να γράψει δική του,
                            το «πατήστε για προσθήκη» θα ήταν ψέμα.
                        </p>
                        <SuggestionsField
                            suggestions={TAG_SUGGESTIONS}
                            value={tags}
                            onAdd={(tag) => setTags((current) => [...current, tag])}
                            allowFree={false}
                            label="Παράδειγμα προτάσεων"
                        />
                    </div>

                    <div>
                        <p className="mb-2 text-sm text-muted">TimeAgo — Intl.RelativeTimeFormat('el')</p>
                        <div className="flex flex-wrap gap-4 text-sm">
                            <span>μόλις τώρα: <TimeAgo date={new Date()} /></span>
                            <span>χθες: <TimeAgo date={new Date(Date.now() - 864e5)} /></span>
                            <span>πέρσι: <TimeAgo date={new Date(Date.now() - 400 * 864e5)} /></span>
                        </div>
                    </div>

                    <div>
                        <p className="mb-2 text-sm text-muted">
                            Pagination — η σελιδοποίηση των μεγάλων καταλόγων (ΜΛΑ-06). Οι τρεις
                            ενδιαφέρουσες καταστάσεις: αρχή, μέση με δύο κενά, τέλος.
                        </p>
                        <Pagination meta={{ page: 1, limit: 20, total: 487, totalPages: 25 }} onPage={() => {}} noun="πόροι" />
                        <Pagination meta={{ page: 13, limit: 20, total: 487, totalPages: 25 }} onPage={() => {}} noun="πόροι" />
                        <Pagination meta={{ page: 25, limit: 20, total: 487, totalPages: 25 }} onPage={() => {}} noun="πόροι" />
                        <p className="mb-2 text-sm text-muted">Μία μόνο σελίδα: μένουν μόνο τα σύνολα.</p>
                        <Pagination meta={{ page: 1, limit: 20, total: 3, totalPages: 1 }} onPage={() => {}} noun="πόροι" />
                    </div>

                    <div>
                        <p className="mb-2 text-sm text-muted">
                            LoadMore και EmptyResult — το LoadMore μένει για κοντούς καταλόγους που
                            δεν φιλτράρονται (σχόλια, σημειώσεις)· οι μεγάλοι κατάλογοι πέρασαν στο
                            Pagination.
                        </p>
                        <LoadMore
                            loaded={20}
                            meta={{ page: 1, totalPages: 3, total: 57 }}
                            isLoading={false}
                            onLoadMore={() => {}}
                            noun="πόροι"
                        />
                        <EmptyResult isSearching emptyMessage="Η συλλογή δεν έχει πόρους ακόμη." />
                    </div>

                    <div>
                        <p className="mb-2 text-sm text-muted">
                            AuthLayout και AuthLink — το κέλυφος των οθονών ταυτοποίησης, σε σμίκρυνση
                        </p>
                        <div className="pointer-events-none max-h-96 origin-top scale-75 overflow-hidden rounded-2xl border border-border">
                            <AuthLayout title="Σύνδεση" subtitle="Δείγμα, όχι λειτουργική οθόνη">
                                <TextField>
                                    <Label>Ηλεκτρονικό ταχυδρομείο</Label>
                                    <Input />
                                </TextField>
                                <AuthLink to="/register">Δημιουργία λογαριασμού</AuthLink>
                            </AuthLayout>
                        </div>
                    </div>
                </div>
            </Section>

            <Section
                finding="Β10"
                title="Εναλλαγή προβολής και ταυτότητα συντάκτη"
                note="Δύο συστατικά που γεννήθηκαν από τον χειροκίνητο έλεγχο. Το ViewTab ζούσε τοπικά στον κατάλογο συλλογών και μετακόμισε στα κοινά όταν το χρειάστηκε και το παράθυρο του πόρου — αντιγραφή θα ήταν η κατηγορία ΣΤ σε μικρογραφία."
            >
                <div className="flex flex-wrap items-center gap-2">
                    <ViewTab icon="fa-solid fa-layer-group" isActive onPress={() => {}}>Ενεργές</ViewTab>
                    <ViewTab icon="fa-solid fa-box-archive" isActive={false} onPress={() => {}}>Αρχειοθετημένες</ViewTab>
                    <ViewTab icon="fa-solid fa-globe" isActive={false} onPress={() => {}}>Δημόσιες</ViewTab>
                </div>

                <div className="mt-5 flex flex-wrap items-start gap-8">
                    <UserBadge user={{ fullName: 'Μαρία Παπαδοπούλου', username: 'mpapadopoulou' }} />
                    <UserBadge user={{ username: 'anonymos' }} />
                    <UserBadge user={{ fullName: 'Με εικόνα', username: 'meikona', avatar: 'https://example.invalid/δεν-φορτώνει.jpg' }} />
                </div>

                {/* Η στοίβα των προσώπων (Σετ Δ): ο ίδιος άνθρωπος που παραπάνω
                    εμφανίζεται με το όνομά του, εδώ σε λίγο χώρο. Το **δεύτερο**
                    δείγμα έχει πέντε πρόσωπα, δηλαδή περισσότερα από όσα
                    δείχνονται: χωρίς αυτό, ο μετρητής «+2» δεν θα εμφανιζόταν
                    ποτέ στη βιβλιοθήκη και η μορφή του δεν θα ελεγχόταν.

                    **Δοκιμάστε τα με `Tab`.** Κάθε πρόσωπο είναι χωριστός
                    στόχος εστίασης και η υπόδειξη με το όνομα ανοίγει και στην
                    εστίαση, όχι μόνο με το ποντίκι. */}
                <div className="mt-5 flex flex-wrap items-center gap-8">
                    <AvatarStack users={AVATAR_STACK_SAMPLE.slice(0, 2)} label={TASKS.assigneeOf} />
                    <AvatarStack users={AVATAR_STACK_SAMPLE} label={TASKS.assigneeOf} />
                </div>

                <p className="mt-3 max-w-2xl text-sm text-muted">
                    Το τρίτο δείγμα έχει <strong className="text-foreground">διεύθυνση εικόνας που δεν φορτώνει</strong>,
                    και είναι το μόνο που ελέγχει κάτι: ο αντικαταστάτης του HeroUI εμφανίζεται μόνο όταν η
                    εικόνα <strong className="text-foreground">αποτύχει</strong>. Με κενό <code>src</code> δεν γίνεται
                    καν απόπειρα φόρτωσης, οπότε δεν υπάρχει αποτυχία και η θέση θα έμενε κενή — γι' αυτό το
                    <code> Avatar.Image</code> αποδίδεται μόνο όταν υπάρχει διεύθυνση.
                </p>
            </Section>

            <Section
                finding="P1-5β"
                title="Τι δεν αποθηκεύτηκε στην προσάρτηση"
                note="Ο πίνακας των απορριφθέντων μεταδεδομένων. Το κουμπί ΓΕΜΙΖΕΙ φόρμα, δεν αποθηκεύει: η D9 δεν παραβιάζεται από την πίσω πόρτα."
            >
                <div className="max-w-2xl">
                    <DiscardedCuration discarded={DISCARDED_SAMPLE} onForward={() => {}} />
                </div>
                {/* Χωρίς κουμπί προώθησης: η ίδια πληροφορία, όταν ο χρήστης δεν
                    έχει δικαίωμα επεξεργασίας του υπάρχοντος πόρου. */}
                <div className="mt-6 max-w-2xl">
                    <DiscardedCuration discarded={DISCARDED_SAMPLE} />
                </div>
            </Section>

            <Section
                finding="P3-2"
                title="Συσχετίσεις πόρων"
                note="ΛΑ-12. Δύο καταστάσεις: με δικαίωμα γραφής και χωρίς. Ο επιλογέας γεμίζει από τους πόρους ΤΗΣ ΙΔΙΑΣ συλλογής — δηλωμένο όριο, όχι ελάττωμα."
            >
                <div className="grid gap-6 xl:grid-cols-2">
                    {/* Με δικαίωμα: ετικέτες, κουμπί αφαίρεσης, επιλογέας. */}
                    <RelatedResources
                        resource={RELATED_SAMPLE.resource}
                        collectionId="design-check"
                        resources={RELATED_SAMPLE.resources}
                        canEdit
                    />
                    {/* Χωρίς δικαίωμα: **η ίδια πληροφορία**, χωρίς καμία ενέργεια.
                        Το ζητούμενο εδώ είναι ακριβώς αυτό που βρήκε ο έλεγχος στις
                        κάρτες των καθηκόντων: η πληροφορία δεν εξαρτάται από
                        δικαίωμα, η ενέργεια εξαρτάται. */}
                    <RelatedResources
                        resource={RELATED_SAMPLE.resource}
                        collectionId="design-check"
                        resources={RELATED_SAMPLE.resources}
                    />
                </div>
                {/* Η κενή κατάσταση, που είναι και η πρώτη που βλέπει ο χρήστης. */}
                <div className="mt-6 max-w-md">
                    <RelatedResources
                        resource={{ id: 'r9', relatedResources: [] }}
                        collectionId="design-check"
                        resources={[]}
                    />
                </div>
            </Section>

            <Section
                finding="P2-3"
                title="Χρηματοδοτικές ευκαιρίες"
                note="Κοινά πεδία καταχώρησης/επεξεργασίας, φίλτρα και σύνοψη ενεργής ή ληγμένης προθεσμίας."
            >
                <div className="grid gap-6 xl:grid-cols-2">
                    <FundingFields value={funding} onChange={setFunding} />
                    <FundingSummary funding={funding} />
                </div>
                <div className="mt-6">
                    <FundingFilters
                        state={fundingFilters}
                        setFilter={(changes) => setFundingFilters((current) => ({ ...current, ...changes }))}
                        showRelevance
                    />
                </div>
            </Section>

            <Section
                finding="Φ2"
                title="Οι τρεις βαθμίδες συνάφειας, και η καταγεγραμμένη εκτίμηση"
                note="Οι τρεις βαθμίδες πρέπει να ξεχωρίζουν με το μάτι. Η χαμηλή ήταν ουδέτερο γκρι και διαβαζόταν ως απουσία εκτίμησης αντί για χαμηλή· είναι πλέον πορτοκαλί, και όχι κόκκινη, γιατί το κόκκινο σημαίνει σφάλμα σε ολόκληρη την εφαρμογή."
            >
                <div className="flex flex-wrap items-center gap-3">
                    <RelevanceChip value="high" />
                    <RelevanceChip value="medium" />
                    <RelevanceChip value="low" />
                </div>

                <div className="mt-5 flex max-w-md flex-col gap-2 rounded-2xl border border-purple-400 bg-purple-100 p-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <i className="fa-solid fa-circle-check text-purple-800" aria-hidden="true" />
                        <span className="text-sm font-semibold text-purple-800">Η εκτίμησή σας καταγράφηκε</span>
                        <RelevanceChip value="medium" />
                    </div>
                    <p className="text-sm">
                        Η αιτιολόγηση σε κανονικό χρώμα κειμένου πάνω σε απαλό μωβ: το ζεύγος
                        ελέγχεται από το build-tokens.mjs στα 7:1.
                    </p>
                </div>

                {/* ⚠ **ΕΔΩ ΗΤΑΝ ΤΡΙΤΟ ΑΝΤΙΓΡΑΦΟ ΤΗΣ ΦΥΣΑΛΙΔΑΣ, ΓΡΑΜΜΕΝΟ ΣΤΟ ΧΕΡΙ.**
                    Η οθόνη που υπάρχει για να **επαληθεύει** τη σχεδίαση έδειχνε
                    μια **απομίμησή** της — με τις ίδιες ακριβώς νεκρές κλάσεις,
                    γιατί αντιγράφηκε από την ίδια πηγή. Άρα δεν μπορούσε ποτέ να
                    αποκαλύψει τη διαφορά: έδειχνε το σφάλμα δίπλα στο σφάλμα και
                    τα δύο συμφωνούσαν.

                    Πλέον αποδίδεται το **πραγματικό** συστατικό. Ό,τι φαίνεται
                    εδώ είναι ό,τι βλέπει ο χρήστης, εξ ορισμού και όχι κατά
                    σύμπτωση. */}
                <div className="mt-5 max-w-md">
                    <ul className="flex flex-col gap-4">
                        <CommentBubble
                            author={{ fullName: 'Μαρία Παπαδοπούλου', username: 'mpapadopoulou' }}
                            text={'Η φυσαλίδα του σχολίου έχει ορατό όριο, και η ημερομηνία κάθεται από κάτω αντί να διεκδικεί τη γραμμή της ταυτότητας.'}
                            createdAt={minutesAgo(120)}
                        />
                        {/* D44: η **ίδια** φυσαλίδα με αναφορά μέσα της. Δίπλα-δίπλα,
                            ώστε να φαίνεται ότι η επισήμανση δεν αλλάζει τίποτε άλλο
                            — ούτε απόσταση γραμμών, ούτε στοίχιση. */}
                        <CommentBubble
                            author={{ fullName: 'Γιώργος Ανδρέου', username: 'gandreou' }}
                            text={'Συμφωνώ με @annamaria — δες και το σχόλιο του @george.papas στην προηγούμενη πρόσκληση.'}
                            createdAt={minutesAgo(35)}
                        />
                    </ul>
                </div>
            </Section>

            <Section
                finding="D44"
                title="Αναφορά χρήστη: η επισήμανση και ο επιλογέας"
                note={
                    'Δύο συστατικά με έναν κανόνα, εισαγόμενο από το άρθρωμα του διακομιστή. ' +
                    'Πάνω: πώς διαβάζεται μια αναφορά — το «@» μένει ορατό, ώστε η πληροφορία να μην είναι το χρώμα. ' +
                    'Κάτω: πληκτρολόγησε «@» μέσα στο πεδίο. Ο κατάλογος ανοίγει με το σκέτο «@», κινείται με τα βελάκια, ' +
                    'δέχεται με Enter ή Tab, κλείνει με Escape — και η εστίαση ΔΕΝ φεύγει από το πεδίο.'
                }
            >
                <div className="flex flex-col gap-5 max-w-xl">
                    <p className="text-sm">
                        <MentionText text="Χωρίς αναφορά: απλό κείμενο, καμία διαφορά." />
                    </p>
                    <p className="text-sm">
                        <MentionText text="Με αναφορές: @annamaria και @george.papas. Μια διεύθυνση όπως hello@example.org ΔΕΝ είναι αναφορά, και ένα σκέτο @ ούτε." />
                    </p>

                    <MentionTextArea
                        value={mentionDraft}
                        onChange={setMentionDraft}
                        members={[
                            { id: '1', username: 'annamaria', fullName: 'Άννα Μαρία Κ.' },
                            { id: '2', username: 'george.papas', fullName: 'Γιώργος Παπάς' },
                            { id: '3', username: 'nasia', fullName: 'Αναστασία Ρ.' }
                        ]}
                        label="Σχόλιο"
                        placeholder="Γράψτε @ για να δείτε τον κατάλογο…"
                        rows={3}
                    />

                    {/* Η κατάσταση αναμονής, που αλλιώς δεν φαίνεται ποτέ: ο
                        κατάλογος μελών φτάνει σε λίγα χιλιοστά του δευτερολέπτου. */}
                    <MentionTextArea
                        label="Όσο τα μέλη δεν έχουν φτάσει"
                        value="@"
                        onChange={() => {}}
                        members={[]}
                        isLoadingMembers
                        rows={2}
                    />
                </div>
            </Section>

            <Section
                finding="Ψ5"
                title="Πεδίο με κουμπί δίπλα, και μήνυμα που δεν το μετακινεί"
                note="Η προφανής διάταξη με στοίχιση στο κάτω άκρο κατεβάζει το κουμπί τη στιγμή που εμφανίζεται το μήνυμα — ακριβώς όταν ο χρήστης πάει να το πατήσει. Εδώ το μήνυμα έχει δική του γραμμή πλέγματος. Πάτα την εναλλαγή και κοίτα το κουμπί."
            >
                <div className="max-w-xl">
                    <FieldWithAction
                        message={showFieldError ? 'Η διεύθυνση είναι υποχρεωτική.' : ''}
                        action={<Button variant="outline">Άντληση στοιχείων</Button>}
                    >
                        {({ describedBy }) => (
                            <TextField
                                value=""
                                onChange={() => {}}
                                isInvalid={showFieldError}
                                aria-describedby={describedBy}
                            >
                                <Label>Διεύθυνση</Label>
                                <Input placeholder="https://..." />
                            </TextField>
                        )}
                    </FieldWithAction>

                    <Button
                        variant="ghost"
                        className="mt-3"
                        onPress={() => setShowFieldError((current) => !current)}
                    >
                        {showFieldError ? 'Απόκρυψη μηνύματος' : 'Εμφάνιση μηνύματος'}
                    </Button>
                </div>
            </Section>

            <Section
                finding="Χ1"
                title="Παράθυρο με περιεχόμενο που δεν χωρά"
                note="Δύο φορές έφτασε σπασμένο στην οθόνη με καθαρό build και πράσινες δοκιμές: τα κουμπιά του υποσέλιδου έμεναν εκτός οθόνης και ήταν απρόσιτα. Είναι εδώ ώστε η κατάσταση να ελέγχεται χωρίς δεδομένα και χωρίς API — άνοιξέ το και προσπάθησε να φτάσεις στα κουμπιά."
            >
                <Button onPress={() => setModalOpen(true)}>Άνοιγμα παραθύρου με πολύ περιεχόμενο</Button>

                {isModalOpen && (
                    <Modal.Root isOpen onOpenChange={setModalOpen}>
                        <Modal.Backdrop>
                            <Modal.Container size="lg" className="sm:max-w-[75vw]">
                                <Modal.Dialog className="w-full sm:max-w-[75vw]">
                                    <Modal.Header>
                                        <Modal.Heading>Παράθυρο ελέγχου κύλισης</Modal.Heading>
                                        <Modal.CloseTrigger />
                                    </Modal.Header>
                                    {/* **Η φόρμα ζει ΜΕΣΑ στο σώμα, ποτέ γύρω από αυτό.**
                                        Τυλιγμένη γύρω από κεφαλίδα, σώμα και υποσέλιδο —όπως
                                        ήταν σε τρία παράθυρα της εφαρμογής— σπάει την αλυσίδα
                                        flex του διαλόγου: το σώμα παύει να είναι άμεσο παιδί
                                        του, χάνει το ύψος του, δεν κυλάει ποτέ, και το
                                        υποσέλιδο βγαίνει έξω από τον διάλογο που το κόβει. */}
                                    <Modal.Body className="flex flex-col gap-3">
                                        <form id="design-check-probe" onSubmit={(event) => { event.preventDefault(); setModalOpen(false); }} className="flex flex-col gap-3">
                                            {Array.from({ length: 40 }, (_, index) => (
                                                <p key={index} className="text-sm">
                                                    {`Γραμμή ${index + 1} από 40. Το περιεχόμενο ξεπερνά σκόπιμα το ύψος της οθόνης, ώστε να φανεί αν το σώμα κυλάει και αν τα κουμπιά μένουν προσιτά.`}
                                                </p>
                                            ))}
                                        </form>
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button variant="outline" onPress={() => setModalOpen(false)}>Ακύρωση</Button>
                                        {/* Το κουμπί μένει στο υποσέλιδο και υποβάλλει τη φόρμα
                                            του σώματος μέσω του `form`: η φόρμα δεν χρειάζεται
                                            να περιβάλλει το κουμπί για να το ακούει. */}
                                        <Button type="submit" form="design-check-probe">Το κουμπί που πρέπει να φτάνεται</Button>
                                    </Modal.Footer>
                                </Modal.Dialog>
                            </Modal.Container>
                        </Modal.Backdrop>
                    </Modal.Root>
                )}
            </Section>

            {/* ── P1-7 ─────────────────────────────────────────────────────────── */}
            <Section
                finding="P1-7"
                title="Γραμμή ροής δραστηριότητας"
                note="Οι τέσσερις καταστάσεις της γραμμής. Η ΤΡΙΤΗ είναι η κρίσιμη: «άλλαξε ΑΛΛΟΥ» διαβάζεται διαφορετικά από «άλλαξε ΕΔΩ», και δεν ονομάζει ποια συλλογή — θα ήταν συναγωγή ύπαρξης (D9). Η τέταρτη δείχνει το σφάλμα ΔΙΠΛΑ στην ενέργεια, γιατί ο κατάλογος κυλάει."
            >
                <ul className="flex max-w-3xl flex-col gap-2">
                    {/* 1. Αναγνωσμένη, μόνο ροή: καμία ενέργεια σήμανσης. */}
                    <ActivityItem entry={ACTIVITY_SAMPLES.read} />
                    {/* 2. Αδιάβαστη, με ενέργεια — η μορφή του καταλόγου ειδοποιήσεων. */}
                    <ActivityItem entry={ACTIVITY_SAMPLES.unread} onMarkRead={() => {}} />
                    {/* 3. Ομαδοποιημένη ενέργεια σε πολλές συλλογές, ΧΩΡΙΣ ονόματα. */}
                    <ActivityItem entry={ACTIVITY_SAMPLES.grouped} onMarkRead={() => {}} />
                    {/* 4. Αποτυχία σήμανσης, με το μήνυμα μέσα στη γραμμή. */}
                    <ActivityItem
                        entry={ACTIVITY_SAMPLES.unread}
                        onMarkRead={() => {}}
                        error="Η σήμανση δεν ολοκληρώθηκε."
                    />
                    {/* 5. **D44**: η γραμμή του αναφερόμενου. Αντιπαράβαλέ την με
                        την πρώτη — ίδιο ρήμα, ίδιο αντικείμενο, ίδιος δράστης, και
                        η μόνη διαφορά είναι γιατί τη βλέπεις. */}
                    <ActivityItem entry={ACTIVITY_SAMPLES.mentioned} onMarkRead={() => {}} />
                </ul>
            </Section>

            {/* ── Α2 ───────────────────────────────────────────────────────────── */}
            <Section
                finding="Α2"
                title="Κουμπί σημασιολογικής εξαγωγής"
                note="Δύο μεγέθη, γιατί ζει σε δύο θέσεις: κάρτα πλαϊνής στήλης και υποσέλιδο παραθύρου. Η ΤΡΙΤΗ κατάσταση είναι η κρίσιμη — το σφάλμα αποδίδεται ΔΙΠΛΑ στο κουμπί και όχι στην κορυφή της σελίδας, γιατί και οι δύο θέσεις ζουν μέσα σε περιοχή με δική της κύλιση. Η διαδρομή εδώ είναι ανύπαρκτη επίτηδες: το πάτημα δείχνει την πραγματική άρνηση."
            >
                <div className="flex max-w-md flex-col gap-4">
                    <ExportJsonLd path="/api/collections/000000000000000000000000/export.jsonld" />
                    <ExportJsonLd path="/api/collections/000000000000000000000000/export.jsonld" size="sm" />
                </div>
            </Section>

            {/* ── Β4 ───────────────────────────────────────────────────────────── */}
            <Section
                finding="Β4"
                title="Ενεργά φίλτρα, με τη γραμμή συμπτυγμένη"
                note="Η περιοχή των φίλτρων συμπτύσσεται, και η σύμπτυξη δεν επιτρέπεται να κρύψει τον λόγο για τον οποίο το αποτέλεσμα είναι μικρό. Κάθε ενεργό φίλτρο μένει ορατό ως αφαιρέσιμο chip. Κοίτα ΚΑΙ σε στενό παράθυρο: η γραμμή πρέπει να αναδιπλώνεται, όχι να κόβεται."
            >
                <div className="flex flex-col gap-4">
                    <ActiveFilters
                        state={readBrowseState('?tags=έρευνα&agency=ΕΛΙΔΕΚ&relevance=high')}
                        setFilter={noop}
                    />
                    <ActiveFilters
                        state={readBrowseState(
                            '?tags=έρευνα,ναυτιλία,βιοϊατρική&agency=Ευρωπαϊκή Επιτροπή'
                            + '&field=Ενέργεια&deadlineBefore=2026-12-31&minAmount=250000'
                            + '&eligible=ΑΕΙ&relevance=medium&from=2026-01-01'
                        )}
                        setFilter={noop}
                    />
                </div>
            </Section>

            {/* ── Β2 ───────────────────────────────────────────────────────────── */}
            <Section
                finding="Β2"
                title="Πίνακας Kanban καθηκόντων"
                note="Τρεις στήλες, από τον κατάλογο καταστάσεων του μοντέλου και με τη σειρά του. Η μεταφορά με το ποντίκι είναι ΣΥΝΤΟΜΕΥΣΗ: ο κύριος δρόμος είναι ο επιλογέας κάθε κάρτας, γιατί η μεταφορά είναι απρόσιτη από πληκτρολόγιο και σιωπηλή στην αφή. Το πρώτο δείγμα είναι του αναδόχου — κάθε κάρτα του σύρεται και έχει επιλογέα. Το δεύτερο είναι τρίτου: μόνο ενδείξεις. Σε στενό παράθυρο οι τρεις στήλες γίνονται στοίβα, ΟΧΙ οριζόντια κύλιση."
            >
                <div className="flex flex-col gap-6">
                    <TaskBoard
                        tasks={BOARD_SAMPLES}
                        currentUserId="u2"
                        canManageOf={noop}
                        onOpenTask={noop}
                        onChangeStatus={noop}
                    />
                    <TaskBoard
                        tasks={BOARD_SAMPLES}
                        currentUserId="u9"
                        canManageOf={noop}
                        onOpenTask={noop}
                        onChangeStatus={noop}
                    />
                </div>
            </Section>

            <Section
                finding="Α6"
                title="Επιστροφή στην κορυφή"
                note="ΤΟ ΚΟΥΜΠΙ ΔΕΝ ΦΑΙΝΕΤΑΙ ΕΔΩ ΠΡΙΝ ΚΥΛΙΣΕΙΣ, ΚΑΙ ΑΥΤΟ ΕΙΝΑΙ ΤΟ ΔΕΙΓΜΑ: κάτω από ένα ύψος παραθύρου η κορυφή είναι ήδη κοντά, οπότε ένα κουμπί που αιωρείται πάνω από κάρτες θα ήταν εμπόδιο και όχι βοήθεια. Κύλισε: εμφανίζεται κάτω δεξιά, με απόσταση από τις άκρες ώστε να μη σκεπάζει ενέργεια. Με Tab μπαίνει στη σειρά εστίασης και δείχνει το κείμενό του — δεν είναι span με title, που σε αφή και πληκτρολόγιο δεν εμφανίζεται ποτέ. Το δεύτερο δείγμα έχει κατώφλι μηδέν, ώστε η όψη του κουμπιού να ελέγχεται χωρίς κύλιση."
            >
                <div className="relative h-24">
                    <BackToTop threshold={0} />
                </div>
            </Section>

            <section>
                <h2 className="mb-3 text-lg font-medium">Βάρη γραμματοσειράς</h2>
                <p className="font-normal">400 — Θεματική περιήγηση ψηφιακών πόρων</p>
                <p className="font-medium">500 — Θεματική περιήγηση ψηφιακών πόρων</p>
                <p className="font-semibold">600 — Θεματική περιήγηση ψηφιακών πόρων</p>
                <p className="font-bold">700 — Θεματική περιήγηση ψηφιακών πόρων</p>
            </section>
        </main>
    );
}
